import React, { useState, useEffect } from 'react';
import { FaPhone, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';

// Liste des pays avec leurs codes, noms et chemins vers les drapeaux
const COUNTRIES = [
    {
        code: 'CM',
        name: 'Cameroun',
        flag: 'https://flagcdn.com/w20/cm.png'
    },
    {
        code: 'SN',
        name: 'Sénégal',
        flag: 'https://flagcdn.com/w20/sn.png'
    },
    {
        code: 'CI',
        name: 'Côte d\'Ivoire',
        flag: 'https://flagcdn.com/w20/ci.png'
    },
    {
        code: 'TG',
        name: 'Togo',
        flag: 'https://flagcdn.com/w20/tg.png'
    },
    {
        code: 'BJ',
        name: 'Bénin',
        flag: 'https://flagcdn.com/w20/bj.png'
    },
    {
        code: 'GN',
        name: 'Guinée',
        flag: 'https://flagcdn.com/w20/gn.png'
    },
    {
        code: 'ML',
        name: 'Mali',
        flag: 'https://flagcdn.com/w20/ml.png'
    },
    {
        code: 'BF',
        name: 'Burkina Faso',
        flag: 'https://flagcdn.com/w20/bf.png'
    },
    {
        code: 'FR',
        name: 'France',
        flag: 'https://flagcdn.com/w20/fr.png'
    },
    {
        code: 'US',
        name: 'États-Unis',
        flag: 'https://flagcdn.com/w20/us.png'
    },
    {
        code: 'GB',
        name: 'Royaume-Uni',
        flag: 'https://flagcdn.com/w20/gb.png'
    }
];

const PhoneNumberInput = ({
                              value,
                              onChange,
                              onValidation,
                              disabled,
                              defaultCountry = 'CM' // Cameroun comme pays par défaut
                          }) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [error, setError] = useState(null);
    const [isValid, setIsValid] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(
        COUNTRIES.find(c => c.code === defaultCountry) || COUNTRIES[0]
    );
    const [isCountrySelectOpen, setIsCountrySelectOpen] = useState(false);

    useEffect(() => {
        if (value) {
            try {
                const phoneNumber = parsePhoneNumber(value);
                if (phoneNumber) {
                    const country = COUNTRIES.find(c => c.code === phoneNumber.country);
                    if (country) {
                        setSelectedCountry(country);
                    }
                    setInputValue(phoneNumber.formatInternational());
                    setIsValid(true);
                    setError(null);
                }
            } catch (e) {
                setInputValue(value);
            }
        }
    }, [value]);

    const handleChange = (e) => {
        const rawValue = e.target.value;
        setInputValue(rawValue);

        try {
            const phoneNumber = parsePhoneNumber(rawValue, selectedCountry.code);
            if (phoneNumber && isValidPhoneNumber(phoneNumber.number)) {
                const formatted = phoneNumber.formatInternational();
                setInputValue(formatted);
                setIsValid(true);
                setError(null);
                onChange(phoneNumber.number);
                onValidation && onValidation(null);
            } else {
                setIsValid(false);
                onChange(rawValue);
            }
        } catch (error) {
            setIsValid(false);
            onChange(rawValue);
        }
    };

    const handleBlur = () => {
        if (inputValue && !isValid) {
            const errorMsg = "Numéro de téléphone invalide";
            setError(errorMsg);
            onValidation && onValidation(errorMsg);
        } else {
            setError(null);
            onValidation && onValidation(null);
        }
    };

    const handleCountrySelect = (country) => {
        setSelectedCountry(country);
        setIsCountrySelectOpen(false);

        if (inputValue) {
            try {
                const phoneNumber = parsePhoneNumber(inputValue, country.code);
                if (phoneNumber && isValidPhoneNumber(phoneNumber.number)) {
                    const formatted = phoneNumber.formatInternational();
                    setInputValue(formatted);
                    setIsValid(true);
                    setError(null);
                    onChange(phoneNumber.number);
                    onValidation && onValidation(null);
                } else {
                    setIsValid(false);
                }
            } catch (error) {
                setIsValid(false);
            }
        }
    };

    return (
        <div className="phone-number-input">
            <div className="input-group mb-3">
                <div className="country-selector-container position-relative">
                    <button
                        className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                        type="button"
                        onClick={() => setIsCountrySelectOpen(!isCountrySelectOpen)}
                        disabled={disabled}
                        style={{ minWidth: '60px', minHeight:'40px', padding: '0.375rem 0.75rem' }}
                    >
                        <img
                            src={selectedCountry.flag}
                            alt={selectedCountry.code}
                            style={{ width: '20px', height: '15px', objectFit: 'cover' }}
                        />
                        {isCountrySelectOpen ? (
                            <FaChevronUp size={12} className="ms-1" />
                        ) : (
                            <FaChevronDown size={12} className="ms-1" />
                        )}
                    </button>

                    {isCountrySelectOpen && (
                        <div className="country-dropdown position-absolute start-0 mt-1 bg-white border rounded shadow-sm z-3"
                             style={{ width: '250px', maxHeight: '300px', overflowY: 'auto', zIndex: 1000, }}>
                            {COUNTRIES.map((country) => (
                                <div
                                    key={country.code}
                                    className={`dropdown-item d-flex align-items-center p-2 ${selectedCountry.code === country.code ? 'bg-light' : ''}`}
                                    onClick={() => handleCountrySelect(country)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <img
                                        src={country.flag}
                                        alt={country.code}
                                        style={{ width: '20px', height: '15px', objectFit: 'cover', marginRight: '10px' }}
                                    />
                                    <span>{country.name}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <input
                    type="tel"
                    value={inputValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${error ? 'is-invalid' : isValid ? 'is-valid' : ''}`}
                    placeholder="Entrez le numéro de téléphone"
                    disabled={disabled}
                />
            </div>

            {error && (
                <div className="invalid-feedback d-flex align-items-center">
                    {error}
                </div>
            )}
        </div>
    );
};

export default PhoneNumberInput;